import { digitsOnlyFilterProps } from '@/shared/lib/tableFilterUtils'
import { useEffect, useMemo, useState } from 'react'
import {
  MRT_PaginationState,
  type MRT_ColumnDef,
  MRT_SortingState,
  MRT_VisibilityState,
  MRT_ColumnFiltersState,
} from 'mantine-react-table'
import { UserListItem } from '@/shared/api/topli/TopliApi'
import { useUserList, useUsersByPage } from '@/entities/User'
import { Badge } from '@mantine/core'
import {
  convertPhoneNumberToString,
  findLabelByValue,
  formatDateForTable,
} from '@/shared/lib/utils'
import { useTranslatedEnums } from '@/shared/helpers/apiEnumTypesOptions'
import { useTranslation } from 'react-i18next'
import { useDebounce } from '@/shared/lib/useDebounce'
import { useLanguageStore } from '@/widgets/LanguageSelect/model/store'
import { useLocation } from 'react-router-dom'
import { useTableFilters } from '@/shared/hooks/useTableFilters'

type UTMDataState = {
  utm_source: boolean
  utm_medium: boolean
  utm_campaign: boolean
}

type ThirdPartyState = {
  google_user_id: boolean
  meta_user_id: boolean
  gclid: boolean
  fbclid: boolean
}

export const useUsersTable = () => {
  const location = useLocation()
  const [search, setSearch] = useState(location?.state?.name || '')
  const [sorting, setSorting] = useState<MRT_SortingState>([])
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 50,
  })
  const [columnVisibility, setColumnVisibility] = useState<MRT_VisibilityState>({
    hash_slug: false,
    utm_source: false,
    utm_medium: false,
    utm_campaign: false,
    google_user_id: false,
    meta_user_id: false,
    gclid: false,
    fbclid: false,
  })

  const { data: personalManagers } = useUserList({
    crm_responsible_id__isnull: false,
  })

  const { userRoleOptions, userStatusOptions } = useTranslatedEnums()
  const { t } = useTranslation(['userManagement', 'common'])
  const { language } = useLanguageStore()
  const debouncedSearch = useDebounce(search, 500)
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([])
  const { getTextFilter, getDateRange, getMultiSelectIds } = useTableFilters(columnFilters)

  const memoizedSorting = useMemo(() => {
    let orderBy = ''
    if (!sorting.length) return null
    sorting.forEach(item => (item.desc ? (orderBy = `-${item.id}`) : (orderBy = `${item.id}`)))
    return orderBy
  }, [sorting])

  const dateRange = getDateRange('created_at')
  const selectedStatusIds = getMultiSelectIds('status')
  const selectedRoleIds = getMultiSelectIds('role')
  const selectedPersonalManagerIds = getMultiSelectIds('personal_manager')
  const debouncedSearchById = useDebounce(getTextFilter('id') || '', 500)
  const debouncedSearchByEmail = useDebounce(getTextFilter('email') || '', 500)
  const debouncedSearchByPhone = useDebounce(getTextFilter('phone') || '', 500)

  const {
    data: users,
    isLoading: isLoadingUsers,
    isError: isErrorUsers,
    isFetching: isFetchingUsers,
  } = useUsersByPage(
    memoizedSorting,
    pagination.pageSize,
    pagination.pageIndex * pagination.pageSize,
    debouncedSearch,
    dateRange,
    selectedStatusIds,
    selectedRoleIds,
    selectedPersonalManagerIds,
    debouncedSearchById,
    debouncedSearchByEmail,
    debouncedSearchByPhone,
  )

  const fetchedUsers = users?.data ?? []
  const totalRowCount = users?.total ?? 0

  useEffect(() => {
    setPagination(prev => ({ ...prev, pageIndex: 0 }))
  }, [debouncedSearch, columnFilters])

  useEffect(() => {
    if (location.state?.name) {
      setSearch(location.state.name || '')
    }
  }, [location.state])

  const handleSearchChange = (value: string) => {
    setSearch(value)
  }

  const columns = useMemo<MRT_ColumnDef<UserListItem>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: t('userManagement:registration_date'),
        filterVariant: 'date-range',
        Cell: ({ cell }) => formatDateForTable(cell.getValue() as string, language),
      },
      {
        accessorKey: 'id',
        header: 'ID',
        filterVariant: 'text',
        size: 90,
        ...digitsOnlyFilterProps(),
      },
      {
        accessorKey: 'hash_slug',
        header: 'Hash',
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        accessorKey: 'fullname',
        enableColumnFilter: false,
        header: t('userManagement:username'),
        Cell: ({ row }) => <div className='text-sm font-semibold'>{row.original.fullname}</div>,
      },
      {
        accessorKey: 'email',
        filterVariant: 'text',
        header: t('userManagement:email'),
      },
      {
        accessorKey: 'phone',
        filterVariant: 'text',
        ...digitsOnlyFilterProps(),
        header: t('userManagement:phone'),
        enableSorting: false,
        Cell: ({ row }) => (
          <div className='text-sm'>{convertPhoneNumberToString(row.original.phone ?? null)}</div>
        ),
      },
      {
        accessorKey: 'role',
        filterFn: 'arrIncludesSome',
        filterVariant: 'multi-select',
        mantineFilterMultiSelectProps: {
          data: userRoleOptions.map(opt => ({
            label: opt.label,
            value: opt.value,
          })),
        },
        header: t('common:library_items.user_role.sing'),
        Cell: ({ row }) => {
          return (
            <Badge size='sm'>{findLabelByValue(userRoleOptions, row.original.role ?? 4)}</Badge>
          )
        },
      },
      {
        accessorKey: 'head_team',
        enableColumnFilter: false,
        header: t('userManagement:team_head'),
        Cell: ({ row }) =>
          row.original?.team?.head_name ? (
            <div className='text-sm font-semibold'>{row.original?.team?.head_name}</div>
          ) : (
            '-'
          ),
      },
      {
        accessorKey: 'personal_manager',
        filterFn: 'arrIncludesSome',
        filterVariant: 'multi-select',
        mantineFilterMultiSelectProps: {
          data: personalManagers?.data?.map(manager => ({
            label: manager.fullname as string,
            value: String(manager.id),
          })),
        },
        header: t('userManagement:personal_manager'),
        Cell: ({ row }) =>
          row.original?.personal_manager ? (
            <div className='text-sm font-semibold'>{row.original?.personal_manager.fullname}</div>
          ) : (
            '-'
          ),
      },
      {
        accessorKey: 'status',
        filterFn: 'arrIncludesSome',
        filterVariant: 'multi-select',
        mantineFilterMultiSelectProps: {
          data: userStatusOptions.map(opt => ({
            label: opt.label,
            value: opt.value,
          })),
        },
        header: t('common:library_items.user_status.sing'),
        Cell: ({ row }) => {
          return (
            <Badge size='sm'>{findLabelByValue(userStatusOptions, row.original.status ?? 4)}</Badge>
          )
        },
      },
      {
        accessorKey: 'city',
        enableColumnFilter: false,
        header: t('userManagement:city'),
        Cell: ({ row }) => <div className='text-sm font-semibold'>{row.original?.city ?? '-'}</div>,
      },
      {
        accessorKey: 'utm_source',
        enableColumnFilter: false,
        header: 'UTM Source',
        Cell: ({ row }) => (
          <div className='text-sm font-semibold'>
            {(row.original?.utm_data as UTMDataState)?.utm_source ?? '-'}
          </div>
        ),
      },
      {
        accessorKey: 'utm_medium',
        enableColumnFilter: false,
        header: 'UTM Medium',
        Cell: ({ row }) => (
          <div className='text-sm font-semibold'>
            {(row.original?.utm_data as UTMDataState)?.utm_medium ?? '-'}
          </div>
        ),
      },
      {
        accessorKey: 'utm_campaign',
        enableColumnFilter: false,
        header: 'UTM Campaign',
        Cell: ({ row }) => (
          <div className='text-sm font-semibold'>
            {(row.original?.utm_data as UTMDataState)?.utm_campaign ?? '-'}
          </div>
        ),
      },
      {
        accessorKey: 'google_user_id',
        enableColumnFilter: false,
        header: 'Google User ID',
        Cell: ({ row }) => (
          <div className='text-sm font-semibold'>
            {(row.original?.third_party as ThirdPartyState)?.google_user_id ?? '-'}
          </div>
        ),
      },
      {
        accessorKey: 'meta_user_id',
        enableColumnFilter: false,
        header: 'Meta User ID',
        Cell: ({ row }) => (
          <div className='text-sm font-semibold'>
            {(row.original?.third_party as ThirdPartyState)?.meta_user_id ?? '-'}
          </div>
        ),
      },
      {
        accessorKey: 'gclid',
        enableColumnFilter: false,
        header: 'Google Click ID',
        Cell: ({ row }) => (
          <div className='text-sm font-semibold'>
            {(row.original?.third_party as ThirdPartyState)?.gclid ?? '-'}
          </div>
        ),
      },
      {
        accessorKey: 'fbclid',
        enableColumnFilter: false,
        header: 'Facebook Click ID',
        Cell: ({ row }) => (
          <div className='text-sm font-semibold'>
            {(row.original?.third_party as ThirdPartyState)?.fbclid ?? '-'}
          </div>
        ),
      },
    ],
    [language, personalManagers?.data],
  )
  return {
    search,
    pagination,
    fetchedUsers,
    totalRowCount,
    columns,
    isErrorUsers,
    isFetchingUsers,
    isLoadingUsers,
    sorting,
    setPagination,
    setSorting,
    handleSearchChange,
    columnVisibility,
    setColumnVisibility,
    columnFilters,
    setColumnFilters,
  }
}
